import React, { useCallback, useEffect, useRef, useState } from "react";

export function Card({
  children,
  bordered = true,
  className = "",
}: {
  children: React.ReactNode;
  bordered?: boolean;
  className?: string;
}) {
  return <div className={`${bordered ? "card" : ""} ${className}`}>{children}</div>;
}

export function Banner({
  kind = "info",
  children,
}: {
  kind?: "info" | "warn" | "error" | "moon";
  children: React.ReactNode;
}) {
  return <div className={`banner ${kind}`}>{children}</div>;
}

export function Spinner() {
  return <span className="spinner" aria-label="loading" />;
}

export function LoadingLine({ message }: { message: string }) {
  return (
    <div className="loading-overlay">
      <Spinner />
      <span>{message}</span>
    </div>
  );
}

/**
 * Drives an async button: tracks pending state + surfaces errors. Mirrors Streamlit's
 * run_with_loading (a button → spinner with a stage message → result).
 */
export function useAsyncAction() {
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (label: string, fn: () => Promise<void> | void) => {
    setPending(true);
    setMessage(label);
    setError(null);
    try {
      await fn();
    } catch (e: any) {
      setError(e?.message ? String(e.message) : String(e));
    } finally {
      setPending(false);
      setMessage("");
    }
  }, []);

  return { pending, message, error, run, setError };
}

export function Expander({
  summary,
  children,
  open = false,
}: {
  summary: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
}) {
  return (
    <details className="tree-node" open={open}>
      <summary>{summary}</summary>
      <div className="tree-body">{children}</div>
    </details>
  );
}

/**
 * A full-screen overlay dialog: dimmed backdrop + centered scrollable card. Closes on the ✕,
 * backdrop click, or Escape. Used for the "Discuss …" input/chat panels.
 */
export function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  // Close only when the click STARTED on the backdrop — selecting text inside the card and
  // releasing over the backdrop must not destroy a chat draft.
  const downOnBackdrop = useRef(false);
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        downOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (downOnBackdrop.current && e.target === e.currentTarget) onClose();
        downOnBackdrop.current = false;
      }}
    >
      <div className="modal-card" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3 className="modal-title">{title}</h3>
          <button className="modal-close" aria-label="Close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
